import { useEffect, useMemo, useRef } from 'react';
import MiniSearch, { SearchResult } from 'minisearch';
/**
 * React hook that search a list of items
 *
 * @param args - Search term, items and min length to search
 * @return Map of the search results or null if search is invalid
 */
export function useSearch({
	search,
	items,
	minLength = 2,
	minScore = 1,
}: Args): Map<string, SearchResult> | null {
	const miniSearch = useRef(
		new MiniSearch({
			fields: ['title', 'text'],
			storeFields: ['title'],
			searchOptions: {
				boost: { title: 2 },
				fuzzy: 0.4,
			},
		}),
	);
	useEffect(() => {
		miniSearch.current.removeAll();
		miniSearch.current.addAll(items);
	}, [items]);

	const response: Map<string, SearchResult> | null = useMemo(() => {
		if (search.length <= minLength) {
			return null;
		}

		if (!miniSearch) {
			return null;
		}

		const { current } = miniSearch;
		if (!current) {
			return null;
		}

		return current.search(search, { fuzzy: 0.2 }).reduce((acc, result) => {
			const { id, score } = result;
			if (score >= minScore) {
				acc.set(id, result);
			}

			return acc;
		}, new Map());
	}, [search, miniSearch, minLength, minScore]);

	return response;
}

interface Args {
	search: string;
	items: Array<SearchItem>;
	minLength?: number;
	minScore?: number;
}

interface SearchItem {
	id: string;
	title: string;
	text: string;
}
